import { produce } from 'immer'
import { shuffle, uniqueId } from 'lodash-es'
import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { OnboardingCharacterInfo } from './helper'
import { NewUserOnboardingContext } from './NewUserOnboardingContext'
import { useNewUserOnboardingFormContext } from './newUserOnboardingForm'

export type ExtendedCharacterInfo = OnboardingCharacterInfo & {
  category: string
  key: string
}

export interface NewUserCharacterOptionsContextValue {
  characterOptions: ExtendedCharacterInfo[]
  initOptions: () => void
  resetOptions: () => void
  addMoreOptions: (baseOption: ExtendedCharacterInfo) => void
}

export const NewUserCharacterOptionsContext =
  createContext<NewUserCharacterOptionsContextValue>({
    characterOptions: [],
    initOptions: () => {},
    resetOptions: () => {},
    addMoreOptions: () => {},
  })

const INIT_CHARACTER_COUNT = 15
const ADD_CHARACTER_IN_CATEGORY_COUNT = 3

const getKey = () => uniqueId('character')

export function NewUserCharacterOptionsProvider(props: PropsWithChildren) {
  const [characterOptions, setCharacterOptions] = useState<
    ExtendedCharacterInfo[]
  >([])
  const { onboardingDataRes } = useContext(NewUserOnboardingContext)
  const { onboardingData } = onboardingDataRes ?? {}
  const form = useNewUserOnboardingFormContext()

  const setupCharacterFromCategories = useCallback(() => {
    setCharacterOptions(characterOptions => {
      // form.values.characters is keyed on tackId since the onboarding switch.
      const selectedTackIds = form.getValues().characters ?? []
      const selectedTackIdSet = new Set(selectedTackIds)
      const selectedOptions: ExtendedCharacterInfo[] = characterOptions.filter(
        character => selectedTackIdSet.has(character.tackId),
      )

      const generatedOptions: ExtendedCharacterInfo[] = []
      const selectedCategories = form.getValues().categories ?? []
      const selectedCategoryCharacters = selectedCategories.map(category => {
        const characterList = onboardingData?.themes?.[category] ?? []
        return {
          category,
          characterList: shuffle(
            characterList
              .filter(character => !selectedTackIdSet.has(character.tackId))
              .map(
                (value: OnboardingCharacterInfo): ExtendedCharacterInfo => ({
                  ...value,
                  category,
                  key: getKey(),
                }),
              ),
          ),
        }
      })
      while (generatedOptions.length < INIT_CHARACTER_COUNT) {
        // avoid lots of characters in the same category
        let noMoreOption = true
        for (const { characterList } of shuffle(selectedCategoryCharacters)) {
          const option = characterList.pop()
          if (option) {
            noMoreOption = false
            generatedOptions.push(option)
          }
          if (generatedOptions.length >= INIT_CHARACTER_COUNT) {
            break
          }
        }
        if (noMoreOption) {
          break
        }
      }
      return [...selectedOptions, ...shuffle(generatedOptions)]
    })
  }, [form.getValues, onboardingData, setCharacterOptions])

  const initializedRef = useRef(false)
  const initOptions = useCallback(() => {
    if (initializedRef.current) {
      return
    }
    setupCharacterFromCategories()
    initializedRef.current = true
  }, [setupCharacterFromCategories])
  const resetOptions = useCallback(() => {
    setupCharacterFromCategories()
  }, [form.setFieldValue, setupCharacterFromCategories])

  const addMoreOptions = useCallback(
    (baseOption: ExtendedCharacterInfo) => {
      setCharacterOptions(
        produce(characterOptions => {
          const index = characterOptions.findIndex(
            option => option.id === baseOption.id,
          )
          if (index < 0) {
            return
          }
          const inOptionCharacters = new Set(
            characterOptions.map(character => character.id),
          )
          const category = baseOption.category
          const characterList = onboardingData?.themes?.[category] ?? []
          const newOptions = shuffle(
            characterList
              .filter(character => !inOptionCharacters.has(character.id))
              .map(
                (value: OnboardingCharacterInfo): ExtendedCharacterInfo => ({
                  ...value,
                  category,
                  key: getKey(),
                }),
              ),
          ).slice(0, ADD_CHARACTER_IN_CATEGORY_COUNT)
          characterOptions.splice(index + 1, 0, ...newOptions)
        }),
      )
    },
    [onboardingData],
  )

  const value = useMemo(
    () => ({
      characterOptions,
      initOptions,
      resetOptions,
      addMoreOptions,
    }),
    [characterOptions, initOptions, resetOptions, addMoreOptions],
  )

  return (
    <NewUserCharacterOptionsContext.Provider value={value}>
      {props.children}
    </NewUserCharacterOptionsContext.Provider>
  )
}
